import { COUNTRIES, type CountryCode, type EntityType } from '@keel/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, useTRPC } from '@/api/query';
import { Button, Dialog, DialogContent, Field, Input, Select, toast } from '@/components/ui';
import { COUNTRY_OPTIONS, CURRENCY_OPTIONS } from '@/lib/countries';
import { DEFAULT_ENTITY_NAME, entityPayload } from '@/lib/entity-defaults';
import { localTerms } from '@/lib/localise';
import { useSession } from '@/lib/session';
import { useAppStore } from '@/lib/store';

const TYPES: readonly EntityType[] = ['individual', 'household', 'sole_trader', 'company', 'partnership', 'trust', 'smsf', 'club'];
const TYPE_LABEL: Record<EntityType, string> = {
  individual: 'Personal', household: 'Household', sole_trader: 'Sole trader', company: 'Company', partnership: 'Partnership', trust: 'Trust',
  smsf: 'SMSF', club: 'Club or association',
};

/** A second set of finances in four fields — no need to go through onboarding again. Switches to it on create. */
export const NewEntityDialog = ({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }) => {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { entity, memberships } = useSession();
  const setEntity = useAppStore((s) => s.setEntity);
  const [type, setType] = useState<EntityType>('sole_trader');
  const [country, setCountry] = useState<CountryCode>((entity?.country as CountryCode | undefined) ?? 'AU');
  const [currency, setCurrency] = useState<string>(COUNTRIES[country]?.currency ?? 'AUD');
  const [name, setName] = useState('');
  const terms = localTerms(country);
  const create = useMutation(trpc.entities.create.mutationOptions({
    onSuccess: async (e) => {
      setEntity(e.id);
      await qc.invalidateQueries();
      // top of the screen: the new-account dialog opens next and its buttons sit where toasts usually land
      toast.success(`${e.name} created`, { description: 'Add its first account next.', position: 'top-center' });
      onClose();
      navigate('/accounts?new=1');
    },
    onError: (e) => toast.error(errorMessage(e)),
  }));
  const submit = () => {
    const body = entityPayload(type, country, name || DEFAULT_ENTITY_NAME[type], memberships.length);
    create.mutate({ ...body, baseCurrency: currency });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="New entity" description="A separate set of finances — a business, a trust or a fund. You can switch between them any time."
        footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={create.isPending} onClick={submit}>Create entity</Button></>}>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <Field label="Type">
            <Select value={type} onValueChange={(v) => setType(v as EntityType)}
              options={TYPES.filter((t) => t !== 'smsf' || terms.smsf).map((t) => ({ value: t, label: t === 'company' ? terms.companyLabel : TYPE_LABEL[t] }))} />
          </Field>
          <Field label="Name" optional>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={DEFAULT_ENTITY_NAME[type]} maxLength={120} />
          </Field>
          <Field label="Country">
            <Select value={country} onValueChange={(v) => { setCountry(v as CountryCode); setCurrency(COUNTRIES[v as CountryCode]?.currency ?? currency); }} options={COUNTRY_OPTIONS} />
          </Field>
          <Field label="Currency">
            <Select value={currency} onValueChange={setCurrency} options={CURRENCY_OPTIONS} />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
};
